import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { buildUniqueNoteFileEntries } from "../../../actions/noteFileNames";

export const appendIncomingFiles = (selectedFiles, newFiles, existingFileNames = []) => {
  const entries = buildUniqueNoteFileEntries(newFiles, [
    ...existingFileNames,
    ...selectedFiles.map((file) => file.name),
  ]);
  return [...selectedFiles, ...entries.map(({ file, fileName }) => file.name === fileName
    ? file
    : new File([file], fileName, { type: file.type, lastModified: file.lastModified }))];
};

export const isIncomingImage = (name, type = "") => type.startsWith("image/") || /\.(jpe?g|png|gif|bmp|svg|webp|avif)$/i.test(name);

const FileThumbnail = ({ file, name, url }) => {
  const [localUrl, setLocalUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    if (!file || !isIncomingImage(name, file.type)) return undefined;
    const objectUrl = URL.createObjectURL(file);
    setLocalUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, name, url]);
  const source = file ? localUrl : url;
  return source && isIncomingImage(name, file ? file.type : "") && !failed
    ? <img src={source} alt="" loading="lazy" onError={() => setFailed(true)} />
    : <i className={`fa ${/\.pdf$/i.test(name) ? "fa-file-pdf-o" : isIncomingImage(name, file ? file.type : "") ? "fa-file-image-o" : "fa-file-o"}`} aria-hidden="true" />;
};

const IncomingFileUpload = ({ id, files, disabled, onChange, onPreview, onRemove, notes = [], urls = {}, onPreviewSaved, onRemoveSaved, canRemoveSaved = false, onUpload, loadingPreviews = false }) => {
  const pickerRef = useRef(null);
  const cameraRef = useRef(null);
  return (
    <div className="incoming-file-upload">
      <div className="incoming-file-heading">
        <strong>Patient files</strong>
        <div className="incoming-file-counts" aria-live="polite">
          {notes.length > 0 && <span>{notes.length} uploaded</span>}
          {loadingPreviews && <span><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true" />Loading previews…</span>}
          {files.length > 0 && <span className="incoming-file-pending-count" role="status">{files.length} file{files.length === 1 ? "" : "s"} selected</span>}
        </div>
      </div>
      <div className="incoming-file-toolbar">
        <input ref={pickerRef} id={id} type="file" multiple className="d-none" aria-label="Choose files (OPD Card, etc.)" disabled={disabled} onChange={onChange} />
        <Button type="button" variant="outline-primary" disabled={disabled} onClick={() => pickerRef.current.click()}>
          <i className="fa fa-paperclip mr-1" aria-hidden="true" />Choose files
        </Button>
        <input ref={cameraRef} id={`${id}-camera`} type="file" accept="image/*" capture="environment"
          className="d-none" aria-label="Camera photo" disabled={disabled} onChange={onChange} />
        <Button type="button" variant="outline-primary" disabled={disabled} onClick={() => cameraRef.current.click()}>
          <i className="fa fa-camera mr-1" aria-hidden="true" />Take photo
        </Button>
        {onUpload && <Button type="button" variant="primary" disabled={disabled || !files.length} onClick={onUpload}>Upload files</Button>}
      </div>
      <small className="incoming-file-help">เลือกได้หลายไฟล์ หรือกด Take photo เพิ่มรูปทีละรูปก่อนบันทึก{onUpload && " · กด Upload files เพื่อบันทึกเฉพาะไฟล์"}</small>
      {files.length || notes.length ? (
        <div className="incoming-file-gallery" role="region" aria-label="Patient attachments" tabIndex="0">
          {files.map((file, index) => (
            <div key={`selected-${file.name}`} className="incoming-file-card incoming-file-card-pending">
              <button type="button" className="btn btn-link incoming-file-open" title={file.name} disabled={disabled} onClick={() => onPreview(file)}>
                <span className="incoming-file-thumbnail"><FileThumbnail file={file} name={file.name} /></span>
                <span className="incoming-file-name">{file.name}</span>
              </button>
              <div className="incoming-file-card-footer">
                <span className="incoming-file-new">New</span>
                <button type="button" className="incoming-file-remove" aria-label={`Remove selected ${file.name}`} disabled={disabled} onClick={() => onRemove(index)}>×</button>
              </div>
            </div>
          ))}
          {notes.map((note) => (
            <div key={note.fileKey} className="incoming-file-card">
              <button type="button" className="btn btn-link incoming-file-open" title={note.filename[0]} disabled={disabled || !urls[note.fileKey]} onClick={() => onPreviewSaved(note)}>
                <span className="incoming-file-thumbnail"><FileThumbnail name={note.filename[0]} url={urls[note.fileKey]} /></span>
                <span className="incoming-file-name">{note.filename[0]}</span>
              </button>
              <div className="incoming-file-card-footer">
                <span className="incoming-file-saved">Uploaded</span>
                {canRemoveSaved && <button type="button" className="incoming-file-remove" aria-label={`Remove ${note.filename[0]} from Incoming Patient`} disabled={disabled} onClick={() => onRemoveSaved(note)}>×</button>}
              </div>
            </div>
          ))}
        </div>
      ) : <div className="incoming-file-empty"><i className="fa fa-files-o mr-2" aria-hidden="true" />ยังไม่มีไฟล์แนบ</div>}
    </div>
  );
};

export default IncomingFileUpload;
